import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Package as PackageIcon, Loader2, Lock } from 'lucide-react';
import { getTrainerPackages } from '../lib/api';
import { CATEGORY_NAMES, formatCurrency, waiverOk } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TrainerPackagesPublic = ({ trainer, trainerPaymentsReady }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isClient, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!trainer?.id) return;
    setLoading(true);
    getTrainerPackages(trainer.id)
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [trainer?.id]);

  if (loading) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading packages…
      </div>
    );
  }

  if (items.length === 0) return null;

  const handleBuy = (pkg) => {
    if (!isAuthenticated || !isClient) {
      toast.message('Sign in as a client to purchase a package');
      navigate('/login');
      return;
    }
    if (!trainerPaymentsReady) {
      toast.error("This trainer isn't accepting payments yet.");
      return;
    }
    if (!waiverOk(user)) {
      toast.message('You\u2019ll need to accept the liability waiver first.');
      return;
    }
    navigate('/checkout/package', {
      state: {
        packageId: pkg.id,
        trainerId: trainer.id,
        trainerName: trainer.full_name,
        trainerImage: trainer?.trainer_profile?.profile_image,
        package: pkg,
      },
    });
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="trainer-packages-public">
      <div className="flex items-center gap-2 mb-4">
        <PackageIcon className="w-5 h-5 text-[#C0A062]" />
        <h2 className="text-xl font-bold text-white">Session Packages</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Save by buying a bundle. Pay once, redeem each session at booking time.
      </p>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="public-packages-list">
        {items.map((p) => {
          const per = p.total_price / Math.max(p.session_count, 1);
          return (
            <div
              key={p.id}
              className="p-4 rounded-lg border border-zinc-700 bg-[#0d0d10] flex flex-col"
              data-testid={`public-package-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {CATEGORY_NAMES[p.category] || p.category} · {p.session_count} sessions
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[#C0A062] text-lg font-bold">{formatCurrency(p.total_price)}</p>
                  <p className="text-[10px] text-zinc-500">{formatCurrency(per)}/session</p>
                </div>
              </div>
              {p.description && (
                <p className="text-xs text-zinc-400 mt-1 line-clamp-3 mb-3">{p.description}</p>
              )}
              <Button
                onClick={() => handleBuy(p)}
                disabled={!trainerPaymentsReady}
                className="mt-auto bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
                data-testid={`buy-package-${p.id}`}
              >
                {trainerPaymentsReady ? 'Buy package' : (<><Lock className="w-3.5 h-3.5 mr-1.5" /> Not accepting yet</>)}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-4">
        Charged upfront via Stripe. Sessions never expire, redeem at any time when booking with this trainer.
      </p>
    </div>
  );
};

export default TrainerPackagesPublic;
